import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Politique des Cookies - Château de Clémentine"
        description="Politique d'utilisation des cookies et technologies similaires sur notre site web."
        canonicalUrl="https://chateaudeclementine.com/cookie-policy"
      />
      
      {/* Header */}
      <div className="bg-gradient-sky py-16">
        <div className="container mx-auto px-4">
          <Link to="/">
            <Button variant="ghost" className="mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour à l'accueil
            </Button>
          </Link>
          <h1 className="text-4xl md:text-5xl font-display font-bold text-foreground">
            Politique des Cookies
          </h1>
          <p className="text-xl text-muted-foreground mt-4">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto prose prose-lg">
          
          <h2>1. Qu'est-ce qu'un cookie ?</h2>
          <p>
            Un cookie est un petit fichier texte déposé sur votre ordinateur, tablette ou smartphone 
            lorsque vous visitez notre site web. Il permet de collecter des informations sur votre navigation 
            et de vous offrir des fonctionnalités adaptées.
          </p>

          <h2>2. Types de cookies utilisés</h2>
          
          <h3>2.1 Cookies strictement nécessaires</h3>
          <p>
            Ces cookies sont indispensables au fonctionnement du site. Ils permettent :
          </p>
          <ul>
            <li>La navigation sur le site</li>
            <li>L'utilisation des fonctionnalités essentielles</li>
            <li>La sécurisation des connexions</li>
          </ul>
          <p><strong>Base légale :</strong> Intérêt légitime (article 6.1.f du RGPD)</p>
          <p><strong>Durée :</strong> Session</p>

          <h3>2.2 Cookies analytiques</h3>
          <p>
            Nous utilisons Google Analytics pour comprendre comment les visiteurs utilisent notre site :
          </p>
          <ul>
            <li><strong>_ga :</strong> Identifie les utilisateurs uniques</li>
            <li><strong>_ga_* :</strong> Maintient l'état de la session</li>
            <li><strong>_gid :</strong> Identifie les utilisateurs uniques</li>
          </ul>
          <p><strong>Base légale :</strong> Consentement (article 6.1.a du RGPD)</p>
          <p><strong>Durée :</strong> 13 mois maximum</p>

          <h3>2.3 Cookies publicitaires</h3>
          <p>
            Ces cookies permettent d'afficher des publicités pertinentes via Google Ads :
          </p>
          <ul>
            <li><strong>_gcl_* :</strong> Mesure les conversions publicitaires</li>
            <li><strong>IDE :</strong> Mesure l'efficacité publicitaire</li>
            <li><strong>test_cookie :</strong> Teste la capacité à accepter les cookies</li>
          </ul>
          <p><strong>Base légale :</strong> Consentement (article 6.1.a du RGPD)</p>
          <p><strong>Durée :</strong> Variable (de quelques heures à 2 ans)</p>

          <h2>3. Finalités du traitement</h2>
          <p>
            Nous utilisons les cookies pour :
          </p>
          <ul>
            <li>Assurer le bon fonctionnement du site</li>
            <li>Analyser l'audience et améliorer l'expérience utilisateur</li>
            <li>Mesurer l'efficacité de nos campagnes publicitaires</li>
            <li>Personnaliser le contenu et les publicités</li>
            <li>Respecter nos obligations légales</li>
          </ul>

          <h2>4. Partenaires tiers</h2>
          <p>
            Nous travaillons avec les partenaires suivants qui peuvent déposer des cookies :
          </p>
          
          <h3>4.1 Google LLC</h3>
          <ul>
            <li><strong>Service :</strong> Google Analytics, Google Ads, Google AdSense</li>
            <li><strong>Localisation :</strong> États-Unis</li>
            <li><strong>Politique de confidentialité :</strong> 
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                https://policies.google.com/privacy
              </a>
            </li>
            <li><strong>Opt-out :</strong> 
              <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
                https://tools.google.com/dlpage/gaoptout
              </a>
            </li>
          </ul>

          <h2>5. Gestion de vos préférences</h2>
          
          <h3>5.1 Paramétrage de votre navigateur</h3>
          <p>
            Vous pouvez configurer votre navigateur pour :
          </p>
          <ul>
            <li>Accepter ou refuser tous les cookies</li>
            <li>Être alerté avant l'enregistrement d'un cookie</li>
            <li>Supprimer les cookies enregistrés</li>
          </ul>

          <h3>5.2 Instructions par navigateur</h3>
          <ul>
            <li><strong>Chrome :</strong> Paramètres {`>`} Confidentialité et sécurité {`>`} Cookies</li>
            <li><strong>Firefox :</strong> Préférences {`>`} Vie privée et sécurité {`>`} Cookies</li>
            <li><strong>Safari :</strong> Préférences {`>`} Confidentialité {`>`} Cookies</li>
            <li><strong>Edge :</strong> Paramètres {`>`} Cookies et autorisations de site</li>
          </ul>

          <h3>5.3 Outils de gestion spécialisés</h3>
          <p>
            Vous pouvez également utiliser :
          </p>
          <ul>
            <li>
              <a href="https://www.youronlinechoices.com/fr/" target="_blank" rel="noopener noreferrer">
                Your Online Choices
              </a> - Gestion des cookies publicitaires
            </li>
            <li>
              <a href="https://optout.aboutads.info/" target="_blank" rel="noopener noreferrer">
                Digital Advertising Alliance
              </a> - Opt-out publicitaire
            </li>
          </ul>

          <h2>6. Conséquences du refus</h2>
          <p>
            Le refus des cookies peut limiter certaines fonctionnalités :
          </p>
          <ul>
            <li>Impossibilité de mesurer l'audience</li>
            <li>Publicités moins pertinentes</li>
            <li>Perte de certaines préférences</li>
          </ul>
          <p>
            Les cookies strictement nécessaires ne peuvent être refusés sans compromettre le fonctionnement du site.
          </p>

          <h2>7. Durée de conservation</h2>
          <p>
            Les durées de conservation varient selon le type de cookie :
          </p>
          <ul>
            <li><strong>Cookies de session :</strong> Supprimés à la fermeture du navigateur</li>
            <li><strong>Cookies persistants :</strong> Conservés pour une durée déterminée (maximum 13 mois pour Google Analytics)</li>
          </ul>

          <h2>8. Transferts internationaux</h2>
          <p>
            Certains cookies peuvent entraîner des transferts de données vers des pays tiers, 
            notamment les États-Unis (Google). Ces transferts sont encadrés par :
          </p>
          <ul>
            <li>Les clauses contractuelles types de la Commission européenne</li>
            <li>Les décisions d'adéquation le cas échéant</li>
            <li>Votre consentement explicite</li>
          </ul>

          <h2>9. Vos droits</h2>
          <p>
            Concernant les données collectées via les cookies, vous disposez des droits suivants :
          </p>
          <ul>
            <li>Droit d'accès</li>
            <li>Droit de rectification</li>
            <li>Droit à l'effacement</li>
            <li>Droit à la limitation du traitement</li>
            <li>Droit à la portabilité</li>
            <li>Droit d'opposition</li>
            <li>Droit de retirer votre consentement</li>
          </ul>
          <p>
            Pour exercer ces droits : lechateaudeclementine@gmail.com
          </p>

          <h2>10. Modifications</h2>
          <p>
            Cette politique peut être modifiée pour s'adapter aux évolutions technologiques et réglementaires. 
            Nous vous informerons de tout changement significatif.
          </p>

          <h2>11. Contact</h2>
          <p>
            Pour toute question sur cette politique des cookies :
          </p>
          <p>
            <strong>Château de Clémentine</strong><br />
            Email : lechateaudeclementine@gmail.com<br />
            Téléphone : +32-123-45-67-89<br />
            Adresse : Avenue de la Rosée 12, 1640 Rhode-Saint-Genèse
          </p>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;